import React, { useState, useEffect } from 'react';
import axios from '../../http';

const SkillChecklistModal = ({ skill, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemCritical, setNewItemCritical] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skill.id]);

  const fetchChecklist = async () => {
    try {
      const res = await axios.get(`/api/admin/skills/${skill.id}/checklist`);
      // Load existing items
      setItems(res.data || []);
    } catch (err) {
      console.error('Failed to load checklist', err);
      alert('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = (e) => {
    e.preventDefault();
    if (!newItemDesc.trim()) return;

    setItems([
      ...items,
      {
        description: newItemDesc,
        is_critical: newItemCritical
      }
    ]);

    // Reset form
    setNewItemDesc('');
    setNewItemCritical(false);
  };

  const handleRemoveItem = (indexToRemove) => {
    setItems(items.filter((_, idx) => idx !== indexToRemove));
  };

  const handleMoveItem = (index, direction) => {
    const newItems = [...items];
    if (direction === 'up' && index > 0) {
      const temp = newItems[index - 1];
      newItems[index - 1] = newItems[index];
      newItems[index] = temp;
    } else if (direction === 'down' && index < newItems.length - 1) {
      const temp = newItems[index + 1];
      newItems[index + 1] = newItems[index];
      newItems[index] = temp;
    }
    setItems(newItems);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`/api/admin/skills/${skill.id}/checklist`, { items });
      onClose(); // Close modal on success
    } catch (err) {
      console.error('Failed to save checklist', err);
      alert('Failed to save checklist');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="wizard-modal"
        style={{ maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}
        >
          <h3 style={{ margin: 0 }}>Edit Steps: {skill.name}</h3>
          <button className="btn btn-sm btn-secondary" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            marginBottom: '1rem',
            borderTop: '1px solid #e2e8f0',
            paddingTop: '1rem'
          }}
        >
          {loading ? (
            <p>Loading steps...</p>
          ) : items.length === 0 ? (
            <p style={{ color: '#718096', fontStyle: 'italic' }}>
              No steps defined yet. Add the first step below.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {items.map((item, idx) => (
                <li
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem',
                    background: '#f7fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    gap: '0.5rem'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <button
                      type="button"
                      onClick={() => handleMoveItem(idx, 'up')}
                      disabled={idx === 0}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: idx === 0 ? 'not-allowed' : 'pointer',
                        color: idx === 0 ? '#cbd5e0' : '#4a5568'
                      }}
                    >
                      &#x25B2;
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveItem(idx, 'down')}
                      disabled={idx === items.length - 1}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: idx === items.length - 1 ? 'not-allowed' : 'pointer',
                        color: idx === items.length - 1 ? '#cbd5e0' : '#4a5568'
                      }}
                    >
                      &#x25BC;
                    </button>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500' }}>{item.description}</div>
                    {item.is_critical && (
                      <span style={{ fontSize: '0.75rem', color: '#e53e3e', fontWeight: 'bold' }}>
                        CRITICAL STEP
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => handleRemoveItem(idx)}
                    style={{ background: '#fed7d7', color: '#c53030', border: 'none' }}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          style={{
            background: '#edf2f7',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}
        >
          <form
            onSubmit={handleAddItem}
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}
          >
            <div style={{ flex: 1 }}>
              <input
                type="text"
                className="form-input"
                placeholder="Describe the step..."
                value={newItemDesc}
                onChange={(e) => setNewItemDesc(e.target.value)}
                style={{ marginBottom: '0.5rem' }}
              />
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <input
                  type="checkbox"
                  checked={newItemCritical}
                  onChange={(e) => setNewItemCritical(e.target.checked)}
                />
                Critical Fail if missed
              </label>
            </div>
            <button type="submit" className="btn btn-secondary" disabled={!newItemDesc.trim()}>
              Add Step
            </button>
          </form>
        </div>

        <div className="modal-actions" style={{ borderTop: 'none', padding: 0, marginTop: 'auto' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Checklist'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillChecklistModal;
